import { z } from 'zod'

/**
 * The record side-panel state machine, encoded entirely in the URL search
 * params (`?panel=<type>&panelId=<id>`) so it is shareable, restorable, and the
 * single source of truth (00-CONTRACT §5: "URL is state"). The shell reads these
 * to decide which panel to mount; module routes call
 * `navigate({ search: { panel: 'contact', panelId: id } })` to open a record
 * panel without leaving the current list route.
 *
 * `agent` is the reserved slot for the agent chat panel (PLAN-03 fills it).
 */
export type PanelType =
  | 'project'
  | 'brand'
  | 'contact'
  | 'company'
  | 'deal'
  | 'subscription'
  | 'invoice'
  | 'order'
  | 'product'
  | 'integration'
  | 'activity'
  | 'engagementEvent'
  | 'analyticsEvent'
  | 'communicationPreference'
  | 'logEntry'
  | 'task'
  | 'booking'
  | 'event_type'
  | 'availability_schedule'
  | 'workflow'
  | 'agent'

export const PANEL_TYPES = [
  'project',
  'brand',
  'contact',
  'company',
  'deal',
  'subscription',
  'invoice',
  'order',
  'product',
  'integration',
  'activity',
  'engagementEvent',
  'analyticsEvent',
  'communicationPreference',
  'logEntry',
  'task',
  'booking',
  'event_type',
  'availability_schedule',
  'workflow',
  'agent',
] as const satisfies readonly PanelType[]

/**
 * Panel mode. `create` opens the panel as a blank draft (no `panelId`) so "New X"
 * happens in the shell side panel, never a modal popup; on save the panel flips
 * to the saved record (panelId set, mode dropped). Absent = view/edit.
 */
export type PanelMode = 'create'

/** Search-param schema validated on every `_authed` route. */
export const panelSearchSchema = z.object({
  panel: z.enum(PANEL_TYPES).optional(),
  panelId: z.string().optional(),
  panelMode: z.enum(['create']).optional(),
  // Cross-reference back-history (JSON `[{panel,panelId},...]`): opening an
  // associated foreign object pushes the current record so a Back button can
  // return to it, all while staying on the SAME list route.
  pstack: z.string().optional(),
})

export type PanelSearch = z.infer<typeof panelSearchSchema>

export interface PanelStackEntry {
  panel: PanelType
  panelId: string
}

/** Parse the `pstack` JSON param into a typed back-history (tolerant). */
export function parsePanelStack(raw: unknown): PanelStackEntry[] {
  if (typeof raw !== 'string' || raw.length === 0) return []
  try {
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.filter(
      (e): e is PanelStackEntry =>
        !!e && typeof e.panel === 'string' && typeof e.panelId === 'string',
    )
  } catch {
    return []
  }
}

/** Serialize a back-history to the `pstack` param (undefined when empty). */
export function serializePanelStack(stack: PanelStackEntry[]): string | undefined {
  return stack.length > 0 ? JSON.stringify(stack) : undefined
}
